import { Injectable } from "@angular/core";
import { Chart, ChartType, Plugin } from "chart.js";
import { SelectItemGroup } from "primeng/api";
import * as moment from "moment";


@Injectable()
export class WidgetChartAuxiliaryService {
    public readonly chartGraphClass: string = "widget-chart-graph";
    public readonly widgetContainerClass: string = "widget-chart-container";

    public getBackgroundPlugin(): Plugin {
        return {
            id: "canvasBackground",
            beforeDraw(chart: Chart<ChartType>, _: { cancelable: true }, options: Record<string, any>): void {
                const { ctx } = chart;
                ctx.save();
                ctx.globalCompositeOperation = "destination-over";
                ctx.fillStyle = options.color || "#f4f4f4";
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        }
    }


    public isFixedWidth(expression: string): boolean {
        return this.isFixedSize(expression, true);
    }

    public isFixedHeight(expression: string): boolean {
        return this.isFixedSize(expression, false);
    }

    public convertRelativeWidthToPx(expression: string): string {
        return this.convertRelativeSizeToPx(expression, 1470, true);
    }

    public convertRelativeHeightToPx(expression: string): string {
        return this.convertRelativeSizeToPx(expression, 1140, false);
    }

    private isFixedSize(expression: string, horizontal: boolean): boolean {
        if (!expression?.length) return false;

        if (!!expression.match(/%\s*$/)) return false;

        return horizontal ? !expression.match(/vw\s*$/) : !expression.match(/vh\s*$/);
    }

    private convertRelativeSizeToPx(expression: string, base: number, horizontal: boolean): string {
        if (!expression?.length) return expression;

        if (this.isFixedSize(expression, horizontal)) return expression;

        const percentMatch: RegExpMatchArray =
            expression.match(/^\s*(?<value>\d+(?:\.\d+)?)\s*%\s*$/);

        if (!!percentMatch) return (parseFloat(percentMatch.groups["value"]) / 100) * base + "px";

        const screenPercentMatch: RegExpMatchArray = horizontal
            ? expression.match(/^\s*(?<value>\d+(?:\.\d+)?)\s*vw\s*$/)
            : expression.match(/^\s*(?<value>\d+(?:\.\d+)?)\s*vh\s*$/);

        if (!!screenPercentMatch) return (parseFloat(screenPercentMatch.groups["value"]) / 100) * base + "px";

        return expression;
    }

    public sortBy(list: any[], property: string): any[] {
        if (!list?.length || !property) return list;

        // If all elements are numbers, sort by value.
        if (!list.some(item => typeof item?.[property] !== "number")) {
            return [...list].sort((a, b) => a[property] - b[property]);
        }

        // If all elements are dates, sort by value.
        if (!list.some(item => !(item?.[property] instanceof Date))) {
            return [...list].sort((a, b) => a[property].getTime() - b[property].getTime());
        }

        // Otherwise sort by string value.
        return list.sort((a, b) => String(a[property]).localeCompare(String(b[property])));
    }

    public groupBy(list: any[], property: string): any[][] {
        if (!list?.length || !property) return [list ?? []];

        const taken: boolean[] = Array(list.length).fill(false);
        const groups: any[][] = [];

        for (let i = 0; i < list.length; i++) {
            if (taken[i]) continue;
            const items: any[] = [];

            for (let j = i; j < list.length; j++) {
                if (list[i]?.[property] === list[j]?.[property]) {
                    taken[j] = true;
                    items.push(list[j]);
                }
            }

            groups.push(items);
        }

        return groups;
    }

    public isNumericBy(list: any[], property: string): boolean {
        if (!list?.length || !property) return false;
        return !list.some(item => typeof item?.[property] !== "number");
    }

    public minBy(list: any[], property: string): number {
        if (!list?.length || !property) return null;
        return Math.min(...list.map(item => item[property]));
    }

    public maxBy(list: any[], property: string): number {
        if (!list?.length || !property) return null;
        return Math.max(...list.map(item => item[property]));
    }

    public toStandardString(list: any[]): string[] {
        if (!list?.length) return list;

        // If all elements are numbers, return them with three decimals of precision.
        if (!list.some(item => typeof item !== "number")) {
            return list.map(item => (<number>item).toLocaleString("en-us", { maximumFractionDigits: 3 }));
        }

        // If all elements are dates, return them in format DD/MM/YYYY.
        if (!list.some(item => !(item instanceof Date))) {
            return list.map(item => moment(item).format("DD/MM/YYYY"));
        }

        // Otherwise, return as string value.
        return list.map(item => String(item));
    }

    public getUnitOptions(): SelectItemGroup[] {
        return [
            {
                label: "Length",
                items: [
                    { label: "Kilometer (Km)", title: "Length", value: "Km" },
                    { label: "Meter (m)", title: "Length", value: "m" },
                    { label: "Centimeter (cm)", title: "Length", value: "cm" },
                    { label: "Millimeter (mm)", title: "Length", value: "mm" },
                    { label: "Foot (ft)", title: "Length", value: "ft" },
                    { label: "Inch (in)", title: "Length", value: "in" },
                ]
            },
            {
                label: "Mass",
                items: [
                    { label: "Kilogram (Kg)", title: "Mass", value: "Kg" },
                    { label: "Gram (g)", title: "Mass", value: "g" },
                    { label: "Milligram (mg)", title: "Mass", value: "mg" },
                    { label: "Pound (lb)", title: "Mass", value: "lb" },
                    { label: "Ounce (oz)", title: "Mass", value: "oz" },
                ]
            },
            {
                label: "Time",
                items: [
                    { label: "Hour (h)", title: "Time", value: "h" },
                    { label: "Minute (min)", title: "Time", value: "min" },
                    { label: "Second (s)", title: "Time", value: "s" },
                ]
            },
            {
                label: "Temperature",
                items: [
                    { label: "Kelvin (°K)", title: "Temperature", value: "°K" },
                    { label: "Celsius (°C)", title: "Temperature", value: "°C" },
                    { label: "Fahrenheit (°F)", title: "Temperature", value: "°F" },
                ]
            },
            {
                label: "Volume",
                items: [
                    { label: "Liter (L)", title: "Volume", value: "L" },
                    { label: "Milliliter (mL)", title: "Volume", value: "mL" },
                    { label: "Gallon (gal)", title: "Volume", value: "gal" },
                    { label: "Fluid Ounce (fl oz)", title: "Volume", value: "fl oz" },
                ]
            },
            {
                label: "Area",
                items: [
                    { label: "Square Meter (m²)", title: "Area", value: "m²" },
                    { label: "Square Kilometer (Km²)", title: "Area", value: "Km²" },
                    { label: "Square Foot (ft²)", title: "Area", value: "ft²" },
                ]
            },
            {
                label: "Velocity",
                items: [
                    { label: "Meter per Second (m/s)", title: "Velocity", value: "m/s" },
                    { label: "Kilometer per hour (Km/h)", title: "Velocity", value: "Km/h" },
                    { label: "Miles per Hour (mph)", title: "Velocity", value: "mph" },
                ]
            },
            {
                label: "Pressure",
                items: [
                    { label: "Pascal (Pa)", title: "Pressure", value: "Pa" },
                    { label: "Atmosphere (atm", title: "Pressure", value: "atm" },
                    { label: "Millimeter of Mercury (mmHg)", title: "Pressure", value: "mmHg" },
                ]
            },
            {
                label: "Energy",
                items: [
                    { label: "Joule (J)", title: "Energy", value: "J" },
                    { label: "Calorie (cal)", title: "Energy", value: "cal" },
                    { label: "Kilowatt-Hour (kWh)", title: "Energy", value: "kWh" },
                ]
            },
            {
                label: "Frequency",
                items: [
                    { label: "Hertz (Hz)", title: "Frequency", value: "Hz" },
                    { label: "Megahertz (MHz)", title: "Frequency", value: "MHz" },
                ]
            },
        ];
    }
}